import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, BACKEND_URL } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Plus, Upload, ShieldCheck, X, Check, FileText as FileIcon, ExternalLink, CheckCircle2, Phone, AlertTriangle, Filter, Search, Copy, Mail, Download as DownloadIcon } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import { FILE_TYPE_KEYS, fileTypeAccept } from "@/lib/fileTypes";
import LeadsTab from "./leads/LeadsTab";

export default function AdminDashboard() {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "leads";

  return (
    <div className="bg-slate-50/40 min-h-screen py-8" data-testid="admin-dashboard">
      <div className="nc-container">
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Admin Console</h1>
        <p className="text-slate-500 mt-1">Manage plans, leads, partners and customers.</p>

        <Tabs defaultValue={defaultTab} className="mt-6">
          <TabsList className="bg-white border border-slate-200" data-testid="admin-tabs">
            <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans">Plans</TabsTrigger>
            <TabsTrigger value="partners" data-testid="tab-partners">Partners</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="withdrawals" data-testid="tab-withdrawals">Withdrawals</TabsTrigger>
            <TabsTrigger value="referrals" data-testid="tab-referrals">Referrals</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="blogs" data-testid="tab-blogs">Blogs</TabsTrigger>
            <TabsTrigger value="careers" data-testid="tab-careers">Careers</TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications">Applications</TabsTrigger>
          </TabsList>
          <TabsContent value="leads"><LeadsTab /></TabsContent>
          <TabsContent value="plans"><PlansTab /></TabsContent>
          <TabsContent value="partners"><PartnersTab /></TabsContent>
          <TabsContent value="customers"><CustomersTab /></TabsContent>
          <TabsContent value="orders"><OrdersTab /></TabsContent>
          <TabsContent value="withdrawals"><WithdrawalsTab /></TabsContent>
          <TabsContent value="referrals"><ReferralsTab /></TabsContent>
          <TabsContent value="analytics"><AnalyticsTab /></TabsContent>
          <TabsContent value="blogs"><BlogsTab /></TabsContent>
          <TabsContent value="careers"><CareersTab /></TabsContent>
          <TabsContent value="applications"><ApplicationsTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-slate-100 last:border-0">
      <span className="text-xs uppercase tracking-wider text-slate-500 col-span-1">{label}</span>
      <span className="col-span-2 text-sm text-slate-800 break-words">{String(value)}</span>
    </div>
  );
}

function PlansTab() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [duplicating, setDuplicating] = useState(null); // plan id being duplicated

  const refresh = useCallback(async () => {
    const { data } = await api.get(`/plans?limit=200${showArchived ? "&include_deleted=true" : ""}`);
    setPlans(data);
  }, [showArchived]);

  useEffect(() => { refresh(); }, [refresh]);

  const uploadFile = async (planId, fileType, file) => {
    const fd = new FormData();
    fd.append("file_type", fileType);
    fd.append("is_premium", "false");
    fd.append("file", file);
    try {
      await api.post(`/plans/${planId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      refresh();
    } catch (err) { alert(err.response?.data?.detail || err.message); }
  };

  const archive = async (id) => {
    if (!window.confirm(
      "Archive this plan? It will be hidden from the site, but past downloads and orders keep working."
    )) return;
    try {
      await api.delete(`/plans/${id}`);
      await refresh();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  const restore = async (id) => {
    try {
      await api.post(`/plans/${id}/restore`);
      await refresh();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  // The copy is created archived, so it won't appear in this list unless
  // "Show archived" is on — go straight to its editor instead of leaving the
  // admin to hunt for it.
  const duplicate = async (id) => {
    setDuplicating(id);
    try {
      const { data } = await api.post(`/plans/${id}/duplicate`);
      navigate(`/admin/plans/${data.id}/edit`);
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setDuplicating(null);
    }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center justify-between w-full">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={showArchived}
                 onChange={e => setShowArchived(e.target.checked)} />
          Show archived plans
        </label>
        <button onClick={() => navigate("/admin/plans/new")} className="nc-btn-primary text-sm py-2">
          <Plus className="w-4 h-4" /> Add New Plan
        </button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Naksha No</Th><Th>Title</Th><Th>Plot</Th><Th>Slug</Th><Th>PDF Bundles</Th><Th>Actions</Th></tr>
            </thead>
            <tbody>
              {plans.map(p => (
                <tr key={p.id} className={`border-b border-slate-100 hover:bg-slate-50/60 ${p.is_deleted ? "opacity-50" : ""}`}>
                  <Td><span className="text-xs font-mono text-slate-600">{p.naksha_no || "—"}</span></Td>
                  <Td>
                    <div className="font-medium">{p.title}</div>
                    {p.title_hi && <div className="text-xs text-slate-500">{p.title_hi}</div>}
                  </Td>
                  <Td>{p.length}×{p.width} / {p.floors}F</Td>
                  <Td><span className="text-xs font-mono text-slate-500">{p.slug || "—"}</span></Td>
                  <Td>
                    <div className="flex gap-1.5 flex-wrap">
                      {FILE_TYPE_KEYS.map(ft => (
                        <FileUploadChip key={ft} planId={p.id} fileType={ft} present={p.files?.some(f => f.file_type === ft)} onUpload={uploadFile} />
                      ))}
                    </div>
                  </Td>
                  <Td>
                    {p.is_deleted ? (
                      <>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 mr-3">Archived</span>
                        <button onClick={() => navigate(`/admin/plans/${p.id}/edit`)} className="text-emerald-700 mr-3 font-medium">Edit</button>
                        <button onClick={() => restore(p.id)} className="text-emerald-700 font-medium">Restore</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => navigate(`/admin/plans/${p.id}/edit`)} className="text-emerald-700 mr-3 font-medium">Edit</button>
                        <button onClick={() => duplicate(p.id)} disabled={duplicating === p.id} className="text-emerald-700 mr-3 font-medium disabled:opacity-50">
                          {duplicating === p.id ? "Duplicating…" : "Duplicate"}
                        </button>
                        <button onClick={() => archive(p.id)} className="text-rose-600">Archive</button>
                      </>
                    )}
                  </Td>
                </tr>
              ))}
              {plans.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">No plans yet. Add your first plan.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FileUploadChip({ planId, fileType, present, onUpload }) {
  const inputRef = React.useRef();
  return (
    <label className={`text-xs px-2 py-1 rounded border cursor-pointer ${present ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`} data-testid={`upload-${fileType}-${planId}`}>
      {present ? <Check className="inline w-3 h-3" /> : <Upload className="inline w-3 h-3" />} {fileType}
      <input ref={inputRef} type="file" accept={fileTypeAccept(fileType)} className="hidden" onChange={(e) => e.target.files?.[0] && onUpload(planId, fileType, e.target.files[0], false)} />
    </label>
  );
}

function PartnersTab() {
  const [partners, setPartners] = useState([]);
  const [filter, setFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [q, setQ] = useState("");
  const [openPartner, setOpenPartner] = useState(null);
  const refresh = () => {
    const params = new URLSearchParams();
    if (filter) params.set("status", filter);
    if (typeFilter) params.set("partner_type", typeFilter);
    if (q) params.set("q", q);
    api.get(`/admin/partners?${params.toString()}`).then(({ data }) => {
      setPartners(data);
      // Refresh open partner if still in list
      if (openPartner) {
        const fresh = data.find(x => x.id === openPartner.id);
        if (fresh) setOpenPartner(fresh);
      }
    });
  };
  useEffect(() => { refresh(); }, [filter, typeFilter, q]);

  const setStatus = async (pid, status) => {
    await api.post(`/admin/partners/${pid}/status?status=${status}`);
    refresh();
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        {["", "pending", "approved", "rejected", "suspended"].map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-sm border ${filter === s ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-600"}`} data-testid={`partner-filter-${s || "all"}`}>{s || "All status"}</button>
        ))}
        <span className="mx-2 text-slate-300">|</span>
        {["", "vendor", "professional"].map(s => (
          <button key={s} onClick={() => setTypeFilter(s)} className={`px-3 py-1.5 rounded-full text-sm border ${typeFilter === s ? "border-sky-500 bg-sky-50 text-sky-800" : "border-slate-200 text-slate-600"}`} data-testid={`partner-type-${s || "any"}`}>{s ? s.charAt(0).toUpperCase() + s.slice(1) : "All types"}</button>
        ))}
        <input className="nc-input max-w-xs py-2 text-sm ml-auto" placeholder="Search material/skill/city..." value={q} onChange={e => setQ(e.target.value)} data-testid="partner-search" />
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Name</Th><Th>Type</Th><Th>Mobile</Th><Th>Location</Th><Th>Items / Skills</Th><Th>Docs</Th><Th>Status</Th><Th>Action</Th></tr>
            </thead>
            <tbody>
              {partners.map(p => (
                <tr key={p.id} className="border-b border-slate-100 align-top hover:bg-slate-50/60 cursor-pointer" data-testid={`partner-row-${p.id}`} onClick={() => setOpenPartner(p)}>
                  <Td><div className="flex items-center gap-2">{p.photo_url && <img src={p.photo_url} alt="" className="w-7 h-7 rounded-full object-cover" />}<span className="font-medium text-emerald-700 hover:underline">{p.name}</span></div></Td>
                  <Td><span className={`text-xs px-2 py-0.5 rounded-full ${p.partner_type === "vendor" ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>{p.partner_type}</span></Td>
                  <Td>{p.mobile}</Td>
                  <Td>{p.state} / {p.district}</Td>
                  <Td className="max-w-[220px]">
                    <div className="flex flex-wrap gap-1">{(p.professions || []).slice(0, 4).map((x, i) => <span key={i} className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{x}</span>)}</div>
                  </Td>
                  <Td>
                    {Object.keys(p.documents || {}).length > 0 ? (
                      <span className="text-xs text-emerald-700 font-medium">{Object.keys(p.documents).length} files</span>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </Td>
                  <Td><span className={`text-xs px-2 py-0.5 rounded-full ${p.status === "approved" ? "bg-emerald-100 text-emerald-700" : p.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{p.status}</span></Td>
                  <Td onClick={e => e.stopPropagation()}>
                    <button onClick={() => setOpenPartner(p)} className="text-emerald-700 mr-2 text-xs font-medium" data-testid={`view-${p.id}`}>View</button>
                    {p.status !== "approved" && <button onClick={() => setStatus(p.id, "approved")} className="text-emerald-700 mr-2 text-xs font-medium" data-testid={`approve-${p.id}`}>Approve</button>}
                    {p.status !== "rejected" && <button onClick={() => setStatus(p.id, "rejected")} className="text-rose-600 text-xs font-medium" data-testid={`reject-${p.id}`}>Reject</button>}
                  </Td>
                </tr>
              ))}
              {partners.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400">No partners match.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {openPartner && (
        <PartnerDetailModal
          partner={openPartner}
          onClose={() => setOpenPartner(null)}
          onStatus={async (status) => { await setStatus(openPartner.id, status); }}
        />
      )}
    </div>
  );
}

function PartnerDetailModal({ partner: p, onClose, onStatus }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const docEntries = Object.entries(p.documents || {});
  const detail = p.details || {};

  // Sections to render based on partner_type
  const isVendor = p.partner_type === "vendor";

  const Row = ({ label, value }) => {
    if (value === null || value === undefined || value === "" || (Array.isArray(value) && !value.length)) return null;
    return (
      <div className="grid grid-cols-3 gap-3 py-2 border-b border-slate-100 last:border-0">
        <span className="text-xs uppercase tracking-wider text-slate-500 col-span-1">{label}</span>
        <span className="col-span-2 text-sm text-slate-800 break-words">{Array.isArray(value) ? value.join(", ") : String(value)}</span>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="partner-detail-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* header */}
        <div className="sticky top-0 bg-white z-10 p-6 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            {p.photo_url
              ? <img src={p.photo_url} alt="" className="w-14 h-14 rounded-xl object-cover border border-slate-200" />
              : <div className="w-14 h-14 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-semibold">{(p.name || "?").slice(0, 2).toUpperCase()}</div>}
            <div>
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{p.name}</h3>
              <p className="text-sm text-slate-500">{p.email || "—"} · {p.mobile}</p>
              <div className="flex gap-2 mt-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${p.partner_type === "vendor" ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>{p.partner_type}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === "approved" ? "bg-emerald-100 text-emerald-700" : p.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{p.status}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="modal-close-btn"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Profile fields */}
          <section>
            <h4 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: "Poppins" }}>Profile</h4>
            <div className="bg-slate-50 rounded-xl p-4">
              <Row label="State / City" value={`${p.state} / ${p.district}`} />
              {isVendor ? (
                <>
                  <Row label="Company" value={detail.company_name} />
                  <Row label="Website" value={detail.website} />
                  <Row label="Office Address" value={detail.office_address} />
                  <Row label="PIN" value={detail.pincode} />
                  <Row label="Vendor Type" value={detail.vendor_type} />
                  <Row label="Materials" value={detail.materials} />
                  <Row label="Custom Material" value={detail.custom_material} />
                  <Row label="Services" value={detail.services} />
                  <Row label="Custom Service" value={detail.custom_service} />
                </>
              ) : (
                <>
                  <Row label="LinkedIn" value={detail.linkedin} />
                  <Row label="Address" value={detail.address} />
                  <Row label="Categories" value={detail.categories} />
                  <Row label="Skills" value={detail.skills} />
                  <Row label="Engagements" value={detail.engagement_types} />
                </>
              )}
              <Row label="Trades / Tags" value={p.professions} />
            </div>
          </section>

          {/* Vendor: Legal + Financial + Experience */}
          {isVendor && (
            <>
              <section>
                <h4 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: "Poppins" }}>Legal & Financial</h4>
                <div className="bg-slate-50 rounded-xl p-4">
                  <Row label="GST" value={detail.gst_number} />
                  <Row label="PAN" value={detail.pan_number} />
                  <Row label="Company Reg" value={detail.company_reg_number} />
                  <Row label="MSME / Udyam" value={detail.msme_number} />
                  <Row label="Trade License" value={detail.trade_license} />
                  <Row label="PF / ESIC" value={detail.pf_esic_number} />
                </div>
              </section>
              <section>
                <h4 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: "Poppins" }}>Financial Strength</h4>
                <div className="bg-slate-50 rounded-xl p-4">
                  <Row label="Turnover Y1" value={detail.turnover_y1} />
                  <Row label="Turnover Y2" value={detail.turnover_y2} />
                  <Row label="Turnover Y3" value={detail.turnover_y3} />
                  <Row label="Bank" value={detail.bank_name} />
                  <Row label="Account Holder" value={detail.account_holder} />
                  <Row label="IFSC" value={detail.ifsc_code} />
                  <Row label="Credit Limit" value={detail.credit_limit} />
                  <Row label="Employees" value={detail.employees} />
                </div>
              </section>
              <section>
                <h4 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: "Poppins" }}>Work Experience</h4>
                <div className="bg-slate-50 rounded-xl p-4">
                  <Row label="Years in Business" value={detail.years_in_business} />
                  <Row label="Major Clients" value={detail.major_clients} />
                  <Row label="Project Types" value={detail.project_types} />
                </div>
              </section>
            </>
          )}

          {/* Professional: Education + Experience */}
          {!isVendor && (
            <>
              <section>
                <h4 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: "Poppins" }}>Education</h4>
                <div className="bg-slate-50 rounded-xl p-4">
                  <Row label="Qualification" value={detail.qualification} />
                  <Row label="Degree" value={detail.degree} />
                  <Row label="College" value={detail.college} />
                  <Row label="Year of Passing" value={detail.year_of_passing} />
                  <Row label="Certifications" value={detail.certifications} />
                </div>
              </section>
              <section>
                <h4 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: "Poppins" }}>Experience</h4>
                <div className="bg-slate-50 rounded-xl p-4">
                  <Row label="Total Years" value={detail.total_experience_years} />
                  <Row label="Current Employer" value={detail.current_employer} />
                  <Row label="Current Designation" value={detail.current_designation} />
                  <Row label="Previous Companies" value={detail.previous_companies} />
                  <Row label="Projects Handled" value={detail.projects_handled} />
                </div>
              </section>
            </>
          )}

          {/* Documents */}
          <section>
            <h4 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: "Poppins" }}>Documents</h4>
            {docEntries.length === 0 ? (
              <p className="text-sm text-slate-400 bg-slate-50 rounded-xl p-4">No documents uploaded yet.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-2">
                {docEntries.map(([key, doc]) => {
                  const url = doc.url && doc.url.startsWith("/api/") ? `${BACKEND}${doc.url}` : doc.url;
                  return (
                    <a key={key}
                       href={url || "#"}
                       target="_blank" rel="noopener noreferrer"
                       className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/30 transition group"
                       data-testid={`doc-link-${key}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                          <FileIcon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{key.replace(/_/g, " ")}</div>
                          <div className="text-xs text-slate-500 truncate">{doc.filename || "—"} · {doc.size ? `${(doc.size / 1024).toFixed(0)} KB` : "?"}</div>
                        </div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-emerald-700 flex-shrink-0" />
                    </a>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        {/* sticky action footer */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4 flex flex-wrap gap-2 justify-end">
          {p.status !== "approved" && <button onClick={() => onStatus("approved")} className="nc-btn-primary text-sm py-2" data-testid="modal-approve-btn"><CheckCircle2 className="w-4 h-4" /> Approve</button>}
          {p.status !== "rejected" && <button onClick={() => onStatus("rejected")} className="px-4 py-2 rounded-lg text-sm font-semibold bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100" data-testid="modal-reject-btn">Reject</button>}
          {p.status !== "suspended" && <button onClick={() => onStatus("suspended")} className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200" data-testid="modal-suspend-btn">Suspend</button>}
          <button onClick={onClose} className="nc-btn-secondary text-sm py-2">Close</button>
        </div>
      </div>
    </div>
  );
}

function WithdrawalsTab() {
  const [rows, setRows] = useState([]);
  const [openAffiliateId, setOpenAffiliateId] = useState(null);
  const [openCustomerId, setOpenCustomerId] = useState(null);
  const refresh = () => api.get("/admin/withdrawals").then(({ data }) => setRows(data));
  useEffect(() => { refresh(); }, []);

  const setStatus = async (id, status) => {
    const note = window.prompt("Admin notes (optional)") || "";
    await api.post(`/admin/withdrawals/${id}/status`, { status, admin_notes: note });
    refresh();
  };

  return (
    <div className="mt-4 bg-white rounded-2xl border border-slate-200 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr><Th>Date</Th><Th>Affiliate</Th><Th>From email</Th><Th>Amount</Th><Th>Status</Th><Th>Notes</Th><Th>Action</Th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-slate-100" data-testid={`withdrawal-row-${r.id}`}>
              <Td>{new Date(r.created_at).toLocaleString()}</Td>
              <Td>
                {r.affiliate?.id ? (
                  <button onClick={() => setOpenAffiliateId(r.affiliate.id)} className="text-emerald-700 hover:underline font-medium text-left" data-testid={`wd-aff-${r.id}`}>
                    {r.affiliate?.name || "—"}
                  </button>
                ) : (<div className="font-medium">{r.affiliate?.name || "—"}</div>)}
                <div className="text-xs text-slate-500">{r.affiliate?.code} • {r.affiliate?.mobile}</div>
              </Td>
              <Td className="text-xs">{r.requested_from_email}</Td>
              <Td className="font-semibold">₹{r.amount_inr.toLocaleString("en-IN")}</Td>
              <Td><span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "paid" ? "bg-emerald-100 text-emerald-700" : r.status === "rejected" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{r.status}</span></Td>
              <Td className="text-xs max-w-[180px]">{r.notes}{r.admin_notes ? <div className="text-slate-500 mt-1">[{r.admin_notes}]</div> : null}</Td>
              <Td>
                {r.status === "pending" && (
                  <>
                    <button onClick={() => setStatus(r.id, "paid")} className="text-emerald-700 mr-2 text-xs font-medium" data-testid={`wd-pay-${r.id}`}>Mark paid</button>
                    <button onClick={() => setStatus(r.id, "rejected")} className="text-rose-600 text-xs font-medium" data-testid={`wd-reject-${r.id}`}>Reject</button>
                  </>
                )}
              </Td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">No withdrawal requests yet.</td></tr>}
        </tbody>
      </table>
      {openAffiliateId && <AffiliateDetailModal affiliateId={openAffiliateId} onClose={() => setOpenAffiliateId(null)} onOpenCustomer={(cid) => { setOpenAffiliateId(null); setOpenCustomerId(cid); }} />}
      {openCustomerId && <CustomerDetailModal customerId={openCustomerId} onClose={() => setOpenCustomerId(null)} />}
    </div>
  );
}

function CustomersTab() {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  useEffect(() => { api.get("/admin/customers").then(({ data }) => setRows(data)); }, []);
  return (
    <div className="mt-4 bg-white rounded-2xl border border-slate-200 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr><Th>Name</Th><Th>Mobile</Th><Th>Email</Th><Th>Downloads</Th><Th>Paid orders</Th><Th>Joined</Th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-slate-100">
              <Td><button onClick={() => setOpenId(r.id)} className="text-emerald-700 hover:underline font-medium text-left" data-testid={`customer-name-${r.id}`}>{r.name || "—"}</button></Td>
              <Td>{r.mobile}</Td><Td>{r.email || "-"}</Td><Td>{r.downloads}</Td><Td>{r.paid_orders}</Td><Td>{r.created_at && new Date(r.created_at).toLocaleDateString()}</Td>
            </tr>
          ))}
        </tbody>
      </table>
      {openId && <CustomerDetailModal customerId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function CustomerDetailModal({ customerId, onClose }) {
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/admin/customers/${customerId}`).then(({ data }) => setC(data)).catch(() => onClose()); }, [customerId]);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="customer-detail-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white z-10 p-6 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{c?.name || "Customer"}</h3>
            <p className="text-sm text-slate-500">{c?.mobile}{c?.email ? ` · ${c.email}` : ""}</p>
            <p className="text-xs text-slate-400 mt-1">Joined {c?.created_at && new Date(c.created_at).toLocaleString()}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {!c ? (
          <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : (
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-3 gap-3">
              <SummaryCard label="Downloads" value={c.stats.downloads} />
              <SummaryCard label="Paid orders" value={c.stats.paid_orders} />
              <SummaryCard label="Total spent" value={`₹${c.stats.total_spent_inr.toLocaleString("en-IN")}`} highlight />
            </div>
            <section>
              <h4 className="font-semibold text-slate-900 mb-2">Orders ({c.orders.length})</h4>
              {c.orders.length === 0 ? <p className="text-sm text-slate-400 bg-slate-50 rounded-xl p-4">No orders.</p> : (
                <div className="overflow-x-auto bg-slate-50 rounded-xl">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="p-2">Order #</th><th className="p-2">Items</th><th className="p-2">Amount</th><th className="p-2">Status</th><th className="p-2">Date</th></tr></thead>
                    <tbody>
                      {c.orders.map(o => (
                        <tr key={o.id} className="border-t border-slate-200">
                          <td className="p-2 font-mono text-xs">{o.order_number || o.id.slice(0, 8)}</td>
                          <td className="p-2">{o.items_count}</td>
                          <td className="p-2 font-medium">₹{o.amount_inr.toLocaleString("en-IN")}</td>
                          <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded-full ${o.status === "paid" ? "bg-emerald-100 text-emerald-700" : o.status === "failed" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>{o.status}</span></td>
                          <td className="p-2 text-xs text-slate-500">{o.created_at && new Date(o.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
            <section>
              <h4 className="font-semibold text-slate-900 mb-2">Recent downloads ({c.downloads.length})</h4>
              {c.downloads.length === 0 ? <p className="text-sm text-slate-400 bg-slate-50 rounded-xl p-4">No downloads.</p> : (
                <div className="overflow-x-auto bg-slate-50 rounded-xl">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="p-2">Plan</th><th className="p-2">File</th><th className="p-2">Date</th></tr></thead>
                    <tbody>
                      {c.downloads.map(d => (
                        <tr key={d.id} className="border-t border-slate-200">
                          <td className="p-2">{d.plan_title || d.plan_id?.slice(0, 8)}</td>
                          <td className="p-2 text-xs"><span className="nc-chip">{d.file_type}</span></td>
                          <td className="p-2 text-xs text-slate-500">{d.timestamp && new Date(d.timestamp).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function AffiliateDetailModal({ affiliateId, onClose, onOpenCustomer }) {
  const [a, setA] = useState(null);
  useEffect(() => { api.get(`/admin/affiliates/${affiliateId}`).then(({ data }) => setA(data)).catch(() => onClose()); }, [affiliateId]);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="affiliate-detail-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white z-10 p-6 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{a?.user?.name || "Affiliate"}</h3>
            <p className="text-sm text-slate-500">{a?.user?.mobile}{a?.user?.email ? ` · ${a.user.email}` : ""}</p>
            {a && <p className="text-xs font-mono text-slate-500 mt-1">Code: {a.code} · {a.commission_pct}% commission</p>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {!a ? (
          <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : (
          <div className="p-6 space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SummaryCard label="Referrals" value={a.stats.total_referrals} />
              <SummaryCard label="Referred revenue" value={`₹${a.stats.total_referred_revenue_inr.toLocaleString("en-IN")}`} />
              <SummaryCard label="Commission earned" value={`₹${a.stats.total_commission_inr.toLocaleString("en-IN")}`} highlight />
              <SummaryCard label="Paid out" value={`₹${a.balance_paid_inr.toLocaleString("en-IN")}`} />
            </div>
            <section>
              <h4 className="font-semibold text-slate-900 mb-2">Payout method</h4>
              <div className="bg-slate-50 rounded-xl p-4">
                <DetailRow label="Method" value={a.payout_method || "—"} />
                {Object.entries(a.payout_details || {}).map(([k, v]) => (
                  <DetailRow key={k} label={k.replace(/_/g, " ")} value={String(v)} />
                ))}
                <DetailRow label="Joined" value={a.created_at && new Date(a.created_at).toLocaleString()} />
              </div>
            </section>
            <section>
              <h4 className="font-semibold text-slate-900 mb-2">Recent referrals ({a.referrals.length})</h4>
              {a.referrals.length === 0 ? <p className="text-sm text-slate-400 bg-slate-50 rounded-xl p-4">No referrals yet.</p> : (
                <div className="overflow-x-auto bg-slate-50 rounded-xl">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="p-2">Customer</th><th className="p-2">Order</th><th className="p-2">Commission</th><th className="p-2">Status</th><th className="p-2">Date</th></tr></thead>
                    <tbody>
                      {a.referrals.map(r => (
                        <tr key={r.id} className="border-t border-slate-200">
                          <td className="p-2">
                            {r.customer_id ? (
                              <button onClick={() => onOpenCustomer && onOpenCustomer(r.customer_id)} className="text-emerald-700 hover:underline font-medium text-left">{r.customer_name || r.customer_mobile || "—"}</button>
                            ) : (r.customer_name || "—")}
                            {r.customer_mobile && <div className="text-xs text-slate-500">{r.customer_mobile}</div>}
                          </td>
                          <td className="p-2 font-medium">₹{r.order_amount_inr.toLocaleString("en-IN")}</td>
                          <td className="p-2 font-semibold text-emerald-700">₹{r.commission_inr.toLocaleString("en-IN")}</td>
                          <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "earned" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{r.status}</span></td>
                          <td className="p-2 text-xs text-slate-500">{r.created_at && new Date(r.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
            <section>
              <h4 className="font-semibold text-slate-900 mb-2">Withdrawals ({a.withdrawals.length})</h4>
              {a.withdrawals.length === 0 ? <p className="text-sm text-slate-400 bg-slate-50 rounded-xl p-4">No withdrawal requests.</p> : (
                <div className="overflow-x-auto bg-slate-50 rounded-xl">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="p-2">Date</th><th className="p-2">Amount</th><th className="p-2">Status</th><th className="p-2">Notes</th></tr></thead>
                    <tbody>
                      {a.withdrawals.map(w => (
                        <tr key={w.id} className="border-t border-slate-200">
                          <td className="p-2 text-xs">{w.created_at && new Date(w.created_at).toLocaleString()}</td>
                          <td className="p-2 font-semibold">₹{w.amount_inr.toLocaleString("en-IN")}</td>
                          <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded-full ${w.status === "paid" ? "bg-emerald-100 text-emerald-700" : w.status === "rejected" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{w.status}</span></td>
                          <td className="p-2 text-xs">{w.notes}{w.admin_notes ? ` [${w.admin_notes}]` : ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

function ReferralsTab() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [code, setCode] = useState("");
  const [openAffiliateId, setOpenAffiliateId] = useState(null);
  const [openCustomerId, setOpenCustomerId] = useState(null);
  const refresh = () => {
    const params = new URLSearchParams();
    if (code) params.set("code", code);
    api.get(`/admin/referrals?${params.toString()}`).then(({ data }) => setRows(data));
    api.get("/admin/referrals/summary").then(({ data }) => setSummary(data));
  };
  useEffect(() => { refresh(); }, [code]);

  return (
    <div className="mt-4 space-y-4" data-testid="referrals-tab">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard label="Successful referrals" value={summary.total_referrals} testid="ref-total" />
          <SummaryCard label="Active affiliates" value={summary.active_affiliates} testid="ref-affiliates" />
          <SummaryCard label="Referred revenue" value={`₹${summary.total_referred_revenue_inr.toLocaleString("en-IN")}`} testid="ref-revenue" highlight />
          <SummaryCard label="Total commission" value={`₹${summary.total_commission_inr.toLocaleString("en-IN")}`} testid="ref-commission" highlight />
        </div>
      )}
      <div className="flex flex-wrap gap-2 items-center">
        <input value={code} onChange={e => setCode(e.target.value)} placeholder="Filter by affiliate code (e.g. NSHA42AB)" className="nc-input max-w-sm py-2 text-sm" data-testid="ref-filter-code" />
        {code && <button onClick={() => setCode("")} className="text-xs text-slate-500 hover:text-slate-700">Clear</button>}
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr><Th>Date</Th><Th>Affiliate</Th><Th>Referred customer</Th><Th>Order</Th><Th>Order amount</Th><Th>Commission</Th><Th>Status</Th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-slate-100" data-testid={`ref-row-${r.id}`}>
                <Td>{r.created_at && new Date(r.created_at).toLocaleString()}</Td>
                <Td>
                  {r.affiliate?.id ? (
                    <button onClick={() => setOpenAffiliateId(r.affiliate.id)} className="text-emerald-700 hover:underline font-medium text-left" data-testid={`ref-aff-${r.id}`}>{r.affiliate?.name || "—"}</button>
                  ) : (<div className="font-medium text-slate-900">{r.affiliate?.name || "—"}</div>)}
                  <div className="text-xs text-slate-500 font-mono">{r.affiliate?.code} · {r.affiliate?.mobile}</div>
                </Td>
                <Td>
                  {r.customer?.id ? (
                    <button onClick={() => setOpenCustomerId(r.customer.id)} className="text-emerald-700 hover:underline font-medium text-left" data-testid={`ref-cust-${r.id}`}>{r.customer?.name || "Unknown"}</button>
                  ) : (<div className="font-medium text-slate-900">{r.customer?.name || "Unknown"}</div>)}
                  <div className="text-xs text-slate-500">{r.customer?.mobile || "—"}{r.customer?.email ? ` · ${r.customer.email}` : ""}</div>
                </Td>
                <Td><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{r.order?.tier || "—"}</span></Td>
                <Td className="font-medium">₹{r.order_amount_inr.toLocaleString("en-IN")}</Td>
                <Td className="font-semibold text-emerald-700">₹{r.commission_inr.toLocaleString("en-IN")}</Td>
                <Td><span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "earned" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{r.status}</span></Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">No successful referrals yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {openAffiliateId && <AffiliateDetailModal affiliateId={openAffiliateId} onClose={() => setOpenAffiliateId(null)} onOpenCustomer={(cid) => { setOpenAffiliateId(null); setOpenCustomerId(cid); }} />}
      {openCustomerId && <CustomerDetailModal customerId={openCustomerId} onClose={() => setOpenCustomerId(null)} />}
    </div>
  );
}

function SummaryCard({ label, value, testid, highlight }) {
  return (
    <div className={`bg-white rounded-2xl border ${highlight ? "border-emerald-300" : "border-slate-200"} p-4`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{value}</div>
    </div>
  );
}

function BlogsTab() {
  const BLANK = { title: "", subtitle: "", slug: "", content: "", excerpt: "", cover_image: "", cover_image_alt: "", youtube_url: "", author: "" };
  const [blogs, setBlogs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [uploading, setUploading] = useState(false);

  const refresh = () => api.get("/admin/blogs").then(({ data }) => setBlogs(data)).catch(() => api.get("/blogs").then(({ data }) => setBlogs(data)));
  useEffect(() => { refresh(); }, []);

  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const slugify = (s) => (s || "")
    .toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  const onTitleChange = (e) => {
    const title = e.target.value;
    setForm((prev) => ({
      ...prev,
      title,
      slug: editing ? prev.slug : (prev.slug ? prev.slug : slugify(title)),
    }));
  };

  const onCoverFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/blogs/upload-cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, cover_image: data.url }));
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!form.title || !form.slug || !form.content) return alert("Title, slug and content are required");
    try {
      if (editing) await api.put(`/admin/blogs/${editing.id}`, form);
      else await api.post("/admin/blogs", form);
      setEditing(null); setForm(BLANK); refresh();
    } catch (err) { alert(err.response?.data?.detail || err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    await api.delete(`/admin/blogs/${id}`);
    refresh();
  };

  return (
    <div className="mt-4 grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Title</Th><Th>Slug</Th><Th>Author</Th><Th>Date</Th><Th>Actions</Th></tr>
            </thead>
            <tbody>
              {blogs.map(b => (
                <tr key={b.id} className="border-b border-slate-100">
                  <Td><div className="font-medium">{b.title}</div><div className="text-xs text-slate-500 line-clamp-1">{b.excerpt}</div></Td>
                  <Td><span className="text-xs font-mono text-slate-500">{b.slug}</span></Td>
                  <Td>{b.author || "—"}</Td>
                  <Td>{b.published_at ? new Date(b.published_at).toLocaleDateString() : "—"}</Td>
                  <Td>
                    <button onClick={() => { setEditing(b); setForm({ title: b.title, subtitle: b.subtitle || "", slug: b.slug, content: b.content, excerpt: b.excerpt || "", cover_image: b.cover_image || "", cover_image_alt: b.cover_image_alt || "", youtube_url: b.youtube_url || "", author: b.author || "" }); }} className="text-emerald-700 mr-2">Edit</button>
                    <button onClick={() => remove(b.id)} className="text-rose-600">Delete</button>
                  </Td>
                </tr>
              ))}
              {blogs.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-400">No blogs yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-2.5 text-sm overflow-y-auto max-h-[85vh]">
        <h3 className="font-semibold mb-1">{editing ? "Edit post" : "New blog post"}</h3>
        <input placeholder="Title *" className="nc-input" value={form.title} onChange={onTitleChange} />
        <input placeholder="Subtitle (optional, shows under title)" className="nc-input" value={form.subtitle} onChange={f("subtitle")} />
        <input placeholder="SEO slug * (e.g. vastu-tips-2026)" className="nc-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} />
        <input placeholder="Author" className="nc-input" value={form.author} onChange={f("author")} />

        <div className="space-y-1.5 rounded-lg border border-slate-200 p-2.5">
          <div className="text-xs font-medium text-slate-600">Featured image</div>
          {form.cover_image ? (
            <div className="flex items-start gap-2">
              <img src={form.cover_image} alt={form.cover_image_alt || "cover preview"} className="w-20 h-20 object-cover rounded border border-slate-200" />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-500 truncate" title={form.cover_image}>{form.cover_image}</div>
                <button type="button" onClick={() => setForm({ ...form, cover_image: "", cover_image_alt: "" })} className="text-xs text-rose-600 mt-1">Remove</button>
              </div>
            </div>
          ) : null}
          <label className="block text-xs text-slate-600 cursor-pointer">
            <span className="inline-block px-3 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 border border-slate-200">
              {uploading ? "Uploading…" : (form.cover_image ? "Replace image" : "Upload image")}
            </span>
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onCoverFile} disabled={uploading} />
          </label>
          <input placeholder="Image alt text (for SEO & accessibility)" className="nc-input" value={form.cover_image_alt} onChange={f("cover_image_alt")} />
        </div>

        <input placeholder="YouTube URL (optional)" className="nc-input" value={form.youtube_url} onChange={f("youtube_url")} />
        <textarea placeholder="Excerpt (short summary)" className="nc-input" rows={2} value={form.excerpt} onChange={f("excerpt")} />
        <div className="space-y-1">
          <div className="text-xs font-medium text-slate-600">Content *</div>
          <RichTextEditor
            key={editing?.id || "new"}
            value={form.content}
            onChange={(html) => setForm((prev) => ({ ...prev, content: html }))}
            onImageUpload={async (file) => {
              const fd = new FormData();
              fd.append("file", file);
              const { data } = await api.post("/admin/blogs/upload-inline", fd, {
                headers: { "Content-Type": "multipart/form-data" },
              });
              return data.url;
            }}
            placeholder="Write the full article — headings, lists, images, links…"
            minHeight={360}
          />
        </div>
        <button onClick={save} className="nc-btn-primary w-full justify-center"><Plus className="w-4 h-4" />{editing ? "Save changes" : "Publish post"}</button>
        {editing && <button onClick={() => { setEditing(null); setForm(BLANK); }} className="nc-btn-secondary w-full justify-center">Cancel</button>}
      </div>
    </div>
  );
}

function CareersTab() {
  const BLANK = { title: "", slug: "", location: "", employment_type: "Full-time", department: "", description: "", apply_email: "", is_active: true };
  const [careers, setCareers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);

  const refresh = () => api.get("/admin/careers").then(({ data }) => setCareers(data)).catch(() => setCareers([]));
  useEffect(() => { refresh(); }, []);

  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.title || !form.slug || !form.description) return alert("Title, slug and description are required");
    try {
      if (editing) await api.put(`/admin/careers/${editing.id}`, form);
      else await api.post("/admin/careers", form);
      setEditing(null); setForm(BLANK); refresh();
    } catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this career posting?")) return;
    await api.delete(`/admin/careers/${id}`);
    refresh();
  };

  const toggleActive = async (c) => {
    try {
      await api.put(`/admin/careers/${c.id}`, {
        title: c.title, slug: c.slug, location: c.location, employment_type: c.employment_type,
        department: c.department, description: c.description, apply_email: c.apply_email,
        is_active: !c.is_active,
      });
      refresh();
    } catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="mt-4 grid lg:grid-cols-3 gap-6" data-testid="careers-tab">
      <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr><Th>Title</Th><Th>Department</Th><Th>Location</Th><Th>Type</Th><Th>Active</Th><Th>Actions</Th></tr>
            </thead>
            <tbody>
              {careers.map(c => (
                <tr key={c.id} className="border-b border-slate-100">
                  <Td>
                    <div className="font-medium">{c.title}</div>
                    <div className="text-xs font-mono text-slate-500">{c.slug}</div>
                  </Td>
                  <Td>{c.department || "—"}</Td>
                  <Td>{c.location || "—"}</Td>
                  <Td>{c.employment_type || "—"}</Td>
                  <Td>
                    <button onClick={() => toggleActive(c)} className={`text-xs px-2 py-0.5 rounded-full ${c.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {c.is_active ? "Active" : "Hidden"}
                    </button>
                  </Td>
                  <Td>
                    <button
                      onClick={() => { setEditing(c); setForm({ title: c.title, slug: c.slug, location: c.location || "", employment_type: c.employment_type || "", department: c.department || "", description: c.description, apply_email: c.apply_email || "", is_active: !!c.is_active }); }}
                      className="text-emerald-700 mr-2">Edit</button>
                    <button onClick={() => remove(c.id)} className="text-rose-600">Delete</button>
                  </Td>
                </tr>
              ))}
              {careers.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">No career postings yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-2.5 text-sm overflow-y-auto max-h-[85vh]">
        <h3 className="font-semibold mb-1">{editing ? "Edit posting" : "New career posting"}</h3>
        <input placeholder="Title * (e.g. Senior Architect)" className="nc-input" value={form.title} onChange={f("title")} />
        <input placeholder="SEO slug * (e.g. senior-architect-noida)" className="nc-input" value={form.slug} onChange={f("slug")} />
        <input placeholder="Department (e.g. Design)" className="nc-input" value={form.department} onChange={f("department")} />
        <input placeholder="Location (e.g. Greater Noida)" className="nc-input" value={form.location} onChange={f("location")} />
        <select className="nc-input" value={form.employment_type} onChange={f("employment_type")}>
          <option value="Full-time">Full-time</option>
          <option value="Part-time">Part-time</option>
          <option value="Contract">Contract</option>
          <option value="Internship">Internship</option>
          <option value="Remote">Remote</option>
        </select>
        <input placeholder="Apply-to email (e.g. careers@nakshacart.com)" className="nc-input" value={form.apply_email} onChange={f("apply_email")} />
        <textarea placeholder="Job description * (responsibilities, requirements, perks)" className="nc-input" rows={10} value={form.description} onChange={f("description")} />
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
          Visible to public
        </label>
        <button onClick={save} className="nc-btn-primary w-full justify-center"><Plus className="w-4 h-4" />{editing ? "Save changes" : "Publish posting"}</button>
        {editing && <button onClick={() => { setEditing(null); setForm(BLANK); }} className="nc-btn-secondary w-full justify-center">Cancel</button>}
      </div>
    </div>
  );
}

const APPLICATION_STATUSES = [
  { value: "new", label: "New", cls: "bg-sky-100 text-sky-700" },
  { value: "reviewing", label: "Reviewing", cls: "bg-amber-100 text-amber-700" },
  { value: "shortlisted", label: "Shortlisted", cls: "bg-emerald-100 text-emerald-700" },
  { value: "rejected", label: "Rejected", cls: "bg-rose-100 text-rose-700" },
  { value: "hired", label: "Hired", cls: "bg-violet-100 text-violet-700" },
];

function statusBadge(s) {
  const cfg = APPLICATION_STATUSES.find((x) => x.value === s) || APPLICATION_STATUSES[0];
  return <span className={`text-xs px-2 py-0.5 rounded-full ${cfg.cls}`}>{cfg.label}</span>;
}

function ApplicationsTab() {
  const [apps, setApps] = useState([]);
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ career_id: "", status: "", q: "", sort: "newest" });
  const [open, setOpen] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.career_id) params.career_id = filters.career_id;
      if (filters.status) params.status = filters.status;
      if (filters.q) params.q = filters.q;
      if (filters.sort) params.sort = filters.sort;
      const { data } = await api.get("/admin/career-applications", { params });
      setApps(data);
    } catch (_) { setApps([]); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/admin/careers").then(({ data }) => setCareers(data)).catch(() => setCareers([]));
  }, []);
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filters]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/career-applications/${id}`, { status });
      setOpen((prev) => prev && prev.id === id ? { ...prev, status } : prev);
      refresh();
    } catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const saveNotes = async (id, admin_notes) => {
    try {
      await api.patch(`/admin/career-applications/${id}`, { admin_notes });
      setOpen((prev) => prev && prev.id === id ? { ...prev, admin_notes } : prev);
      refresh();
    } catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application and its resume?")) return;
    await api.delete(`/admin/career-applications/${id}`);
    setOpen(null);
    refresh();
  };

  const downloadResume = async (a) => {
    try {
      const res = await api.get(`/admin/career-applications/${a.id}/resume`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.resume_filename || `resume-${a.id}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="mt-4 space-y-4" data-testid="applications-tab">
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-medium text-slate-600">Search</label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <input
              className="nc-input pl-8"
              placeholder="Name, email, mobile, role…"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              data-testid="apps-search"
            />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Position</label>
          <select
            className="nc-input mt-1"
            value={filters.career_id}
            onChange={(e) => setFilters({ ...filters, career_id: e.target.value })}
            data-testid="apps-filter-career"
          >
            <option value="">All positions</option>
            <option value="__open__" disabled>— Open applications below —</option>
            {careers.map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Status</label>
          <select
            className="nc-input mt-1"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            data-testid="apps-filter-status"
          >
            <option value="">All statuses</option>
            {APPLICATION_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Sort</label>
          <select
            className="nc-input mt-1"
            value={filters.sort}
            onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
            data-testid="apps-sort"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="name">Name (A→Z)</option>
            <option value="status">Status</option>
          </select>
        </div>
        <button
          type="button"
          onClick={() => setFilters({ career_id: "", status: "", q: "", sort: "newest" })}
          className="text-sm text-slate-600 underline"
        >
          Reset
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>Applicant</Th>
                <Th>Position</Th>
                <Th>Contact</Th>
                <Th>Experience</Th>
                <Th>Location</Th>
                <Th>Status</Th>
                <Th>Submitted</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="p-8 text-center text-slate-400"><Loader2 className="animate-spin inline" /></td></tr>
              )}
              {!loading && apps.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-slate-400">No applications match these filters.</td></tr>
              )}
              {!loading && apps.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`app-row-${a.id}`}>
                  <Td>
                    <div className="font-medium">{a.full_name}</div>
                    {a.current_role && <div className="text-xs text-slate-500">{a.current_role}</div>}
                  </Td>
                  <Td>{a.career_title || <span className="text-slate-400">Open application</span>}</Td>
                  <Td>
                    <div className="text-xs">{a.email}</div>
                    <div className="text-xs text-slate-500">{a.mobile}</div>
                  </Td>
                  <Td>{a.experience_years ? `${a.experience_years} yrs` : "—"}</Td>
                  <Td>{a.location || "—"}</Td>
                  <Td>{statusBadge(a.status)}</Td>
                  <Td>{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</Td>
                  <Td>
                    <button onClick={() => setOpen(a)} className="text-emerald-700 mr-2" data-testid={`app-view-${a.id}`}>View</button>
                    {a.has_resume && (
                      <button onClick={() => downloadResume(a)} className="text-sky-700 mr-2" data-testid={`app-resume-${a.id}`}>CV</button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => setOpen(null)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-2xl my-8 shadow-xl border border-slate-200"
            onClick={(e) => e.stopPropagation()}
            data-testid="app-detail-modal"
          >
            <div className="flex items-start justify-between p-5 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{open.full_name}</h3>
                <p className="text-sm text-slate-500">{open.career_title || "Open application"}</p>
              </div>
              <button onClick={() => setOpen(null)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div><div className="text-xs uppercase tracking-wider text-slate-500">Email</div><div className="font-medium">{open.email}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-slate-500">Mobile</div><div className="font-medium">{open.mobile}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-slate-500">Location</div><div>{open.location || "—"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-slate-500">Experience</div><div>{open.experience_years ? `${open.experience_years} yrs` : "—"}</div></div>
                <div className="sm:col-span-2"><div className="text-xs uppercase tracking-wider text-slate-500">Current role / employer</div><div>{open.current_role || "—"}</div></div>
              </div>

              {open.cover_letter && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Cover letter</div>
                  <div className="text-sm text-slate-800 whitespace-pre-line bg-slate-50 border border-slate-200 rounded-lg p-3">{open.cover_letter}</div>
                </div>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                {open.has_resume ? (
                  <button onClick={() => downloadResume(open)} className="nc-btn-secondary text-sm">
                    <DownloadIcon className="w-4 h-4" /> Download CV {open.resume_filename ? `(${open.resume_filename})` : ""}
                  </button>
                ) : (
                  <span className="text-sm text-slate-500">No CV attached</span>
                )}
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">Status</div>
                <div className="flex flex-wrap gap-2">
                  {APPLICATION_STATUSES.map((s) => (
                    <button
                      key={s.value}
                      onClick={() => updateStatus(open.id, s.value)}
                      className={`text-xs px-3 py-1 rounded-full border ${open.status === s.value ? s.cls + " border-transparent ring-2 ring-offset-1 ring-emerald-500/30" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                      data-testid={`app-status-${s.value}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <NotesEditor key={open.id} initial={open.admin_notes || ""} onSave={(v) => saveNotes(open.id, v)} />

              <div className="pt-2 border-t border-slate-200 flex justify-between">
                <button onClick={() => remove(open.id)} className="text-rose-600 text-sm">Delete application</button>
                <button onClick={() => setOpen(null)} className="nc-btn-secondary text-sm">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NotesEditor({ initial, onSave }) {
  const [val, setVal] = useState(initial);
  const [saving, setSaving] = useState(false);
  const dirty = val !== initial;
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-1.5">Admin notes (internal)</div>
      <textarea
        className="nc-input"
        rows={3}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="Interview feedback, screening notes…"
        data-testid="app-notes"
      />
      <button
        onClick={async () => { setSaving(true); await onSave(val); setSaving(false); }}
        disabled={!dirty || saving}
        className="mt-2 text-sm text-emerald-700 disabled:text-slate-400"
        data-testid="app-notes-save"
      >
        {saving ? "Saving…" : (dirty ? "Save notes" : "Saved")}
      </button>
    </div>
  );
}

function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(({ data }) => setData(data)); }, []);
  if (!data) return <div className="mt-4 text-slate-400"><Loader2 className="animate-spin" /></div>;

  const cards = [
    { l: "Plans", v: data.plans }, { l: "Customers", v: data.customers },
    { l: "Approved Partners", v: data.partners }, { l: "Total Leads", v: data.leads_total },
    { l: "Leads Assigned", v: data.leads_assigned }, { l: "Downloads", v: data.downloads },
    { l: "Paid Orders", v: data.paid_orders }, { l: "Revenue (₹)", v: data.revenue_inr?.toLocaleString("en-IN") },
  ];

  const barData = [
    { name: "Plans", value: data.plans },
    { name: "Customers", value: data.customers },
    { name: "Partners", value: data.partners },
    { name: "Leads", value: data.leads_total },
    { name: "Downloads", value: data.downloads },
    { name: "Orders", value: data.paid_orders },
  ];

  const { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } = require("recharts");

  return (
    <div className="mt-4 space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map(c => (
          <div key={c.l} className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="text-xs uppercase tracking-wider text-slate-500">{c.l}</div>
            <div className="text-2xl font-semibold text-slate-900 mt-2" style={{ fontFamily: "Poppins" }}>{c.v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Overview</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={barData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} />
            <YAxis tick={{ fontSize: 12, fill: "#64748b" }} />
            <Tooltip contentStyle={{ borderRadius: "12px", border: "1px solid #e2e8f0", fontSize: 13 }} />
            <Bar dataKey="value" fill="#10b981" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ───────────────────────── Orders Tab ─────────────────────────
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "all", q: "", flagged: false, date_from: "", date_to: "" });
  const [openOrder, setOpenOrder] = useState(null);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== "all") params.set("status", filters.status);
      if (filters.q.trim()) params.set("q", filters.q.trim());
      if (filters.flagged) params.set("flagged", "true");
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      const { data } = await api.get(`/admin/orders?${params.toString()}`);
      setOrders(data);
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchOrders(); /* initial */ }, []);

  const counts = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    if (o.risk_flags?.length) acc.flagged = (acc.flagged || 0) + 1;
    return acc;
  }, {});

  const fmtINR = (paise) => "₹" + ((paise || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  return (
    <div className="mt-4 space-y-4" data-testid="orders-tab">
      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Total" value={orders.length} />
        <StatCard label="Paid" value={orders.filter(o => o.status === "paid").length} tone="emerald" />
        <StatCard label="Created (pending)" value={orders.filter(o => o.status === "created").length} tone="amber" />
        <StatCard label="Failed" value={orders.filter(o => o.status === "failed").length} tone="rose" />
        <StatCard label="Flagged" value={counts.flagged || 0} tone="rose" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        <div className="md:col-span-3">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</label>
          <select className="nc-input mt-1" value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} data-testid="orders-filter-status">
            <option value="all">All statuses</option>
            <option value="paid">Paid</option>
            <option value="created">Created (incomplete)</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div className="md:col-span-3">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Search</label>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input className="nc-input pl-9" placeholder="Order#, mobile, email, name…" value={filters.q}
              onChange={e => setFilters({ ...filters, q: e.target.value })}
              onKeyDown={e => e.key === "Enter" && fetchOrders()}
              data-testid="orders-filter-search" />
          </div>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">From</label>
          <input type="date" className="nc-input mt-1" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">To</label>
          <input type="date" className="nc-input mt-1" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} />
        </div>
        <div className="md:col-span-2 flex flex-col gap-2">
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={filters.flagged} onChange={e => setFilters({ ...filters, flagged: e.target.checked })} />
            Risk-flagged only
          </label>
          <button onClick={fetchOrders} disabled={loading} className="nc-btn-primary justify-center text-sm py-2" data-testid="orders-apply-filters">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Filter className="w-4 h-4" />} Apply
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>Order #</Th><Th>Date</Th><Th>Customer</Th><Th>Items</Th>
                <Th>Amount</Th><Th>Status</Th><Th>Downloaded</Th><Th>Risk</Th><Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setOpenOrder(o)} data-testid={`order-row-${o.id}`}>
                  <Td className="font-mono text-xs">{o.order_number || <span className="text-slate-400">— legacy —</span>}</Td>
                  <Td className="text-xs whitespace-nowrap">{o.created_at ? new Date(o.created_at).toLocaleString() : "-"}</Td>
                  <Td>
                    <div className="font-medium text-slate-900">{o.billing_name || "—"}</div>
                    <div className="text-xs text-slate-500">{o.billing_mobile || "no mobile"}{o.billing_email ? ` • ${o.billing_email}` : ""}</div>
                    {o.is_business && o.business_name && <div className="text-xs text-emerald-700">🏢 {o.business_name}</div>}
                  </Td>
                  <Td className="text-xs">{o.total_items} item{o.total_items !== 1 ? "s" : ""}</Td>
                  <Td className="font-semibold whitespace-nowrap">{fmtINR(o.amount)}</Td>
                  <Td><StatusBadge status={o.status} /></Td>
                  <Td>
                    {o.status === "paid" ? (
                      <DownloadBadge done={o.downloaded_items} total={o.total_items} />
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </Td>
                  <Td>
                    {o.risk_flags?.length ? (
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                        o.risk_severity === "high" ? "bg-rose-100 text-rose-700"
                          : o.risk_severity === "medium" ? "bg-amber-100 text-amber-800"
                          : o.risk_severity === "low" ? "bg-sky-100 text-sky-700"
                          : "bg-slate-100 text-slate-600"
                      }`}>
                        <AlertTriangle className="w-3 h-3" /> {o.risk_flags.length}
                      </span>
                    ) : <span className="text-xs text-slate-400">clean</span>}
                  </Td>
                  <Td>
                    {o.status === "failed" && o.billing_mobile && (
                      <a href={`tel:+91${o.billing_mobile}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline" onClick={e => e.stopPropagation()}>
                        <Phone className="w-3 h-3" /> Call
                      </a>
                    )}
                  </Td>
                </tr>
              ))}
              {orders.length === 0 && !loading && (
                <tr><td colSpan={9} className="p-8 text-center text-slate-400 text-sm">No orders match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {openOrder && <OrderDetailDrawer orderId={openOrder.id} initialOrder={openOrder} onClose={() => setOpenOrder(null)} />}
    </div>
  );
}

function StatCard({ label, value, tone = "slate" }) {
  const tones = {
    slate: "text-slate-900", emerald: "text-emerald-700", amber: "text-amber-700", rose: "text-rose-700",
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${tones[tone]}`} style={{ fontFamily: "Poppins" }}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid: "bg-emerald-100 text-emerald-700",
    created: "bg-amber-100 text-amber-800",
    failed: "bg-rose-100 text-rose-700",
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${map[status] || "bg-slate-100 text-slate-600"}`}>{status}</span>;
}

function DownloadBadge({ done, total }) {
  if (total === 0) return <span className="text-xs text-slate-400">—</span>;
  if (done === 0) return <span className="text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 font-medium">0/{total} ⚠</span>;
  if (done < total) return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 font-medium">{done}/{total}</span>;
  return <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">✓ {done}/{total}</span>;
}

function OrderDetailDrawer({ orderId, initialOrder, onClose }) {
  const [order, setOrder] = useState(initialOrder);
  useEffect(() => {
    api.get(`/admin/orders/${orderId}`).then(({ data }) => setOrder(data)).catch(() => {});
  }, [orderId]);
  if (!order) return null;
  const fmtINR = (p) => "₹" + ((p || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  const Section = ({ title, children }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{title}</h4>
      {children}
    </div>
  );
  const KV = ({ k, v }) => (
    <div className="flex items-start justify-between gap-3 text-sm py-1">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-900 font-medium text-right break-all">{v ?? "—"}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose} data-testid="order-detail-drawer">
      <div className="flex-1 bg-black/40" />
      <div className="w-full max-w-xl bg-slate-50 h-full overflow-y-auto p-5 space-y-4 shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500">{order.created_at && new Date(order.created_at).toLocaleString()}</div>
            <h3 className="text-lg font-semibold text-slate-900 font-mono">{order.order_number || `id ${order.id.slice(0, 8)}…`}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-200" aria-label="close"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-2">
          <StatusBadge status={order.status} />
          {order.status === "paid" && <DownloadBadge done={order.downloaded_items} total={order.total_items} />}
          {order.is_business && <span className="text-xs px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 font-medium">Business</span>}
        </div>

        {/* Risk flags */}
        {order.risk_flags?.length > 0 && (
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 space-y-2" data-testid="order-risk-flags">
            <div className="flex items-center gap-2 text-rose-800 font-semibold text-sm">
              <AlertTriangle className="w-4 h-4" /> Fraud / risk signals
            </div>
            <ul className="space-y-1 text-sm text-rose-900">
              {order.risk_flags.map(f => (
                <li key={f.code} className="flex gap-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${
                    f.severity === "high" ? "bg-rose-700 text-white"
                      : f.severity === "medium" ? "bg-amber-600 text-white"
                      : f.severity === "low" ? "bg-sky-600 text-white"
                      : "bg-slate-500 text-white"
                  }`}>{f.severity}</span>
                  {f.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* CX actions for failed orders */}
        {order.status === "failed" && order.billing_mobile && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900">Reach out to recover this customer</p>
              <p className="text-xs text-amber-800">Failure reason: {order.failure_reason || "unknown"}</p>
            </div>
            <a href={`tel:+91${order.billing_mobile}`} className="nc-btn-primary text-sm py-2"><Phone className="w-4 h-4" /> Call</a>
            {order.billing_email && (
              <a href={`mailto:${order.billing_email}`} className="nc-btn-secondary text-sm py-2"><Mail className="w-4 h-4" /></a>
            )}
          </div>
        )}

        <Section title="Buyer">
          <KV k="Name" v={order.billing_name} />
          <KV k="Mobile" v={order.billing_mobile && (
            <span className="inline-flex items-center gap-2">
              {order.billing_mobile}
              <a href={`tel:+91${order.billing_mobile}`} className="text-emerald-700"><Phone className="w-3.5 h-3.5" /></a>
            </span>
          )} />
          <KV k="Email" v={order.billing_email && (
            <a href={`mailto:${order.billing_email}`} className="text-emerald-700 underline">{order.billing_email}</a>
          )} />
          <KV k="Address" v={`${order.billing_address}, ${order.billing_city}, ${order.billing_state} ${order.billing_pincode}`} />
          {order.is_business && <>
            <KV k="Business" v={order.business_name} />
            <KV k="GSTIN" v={order.gstin} />
          </>}
          {order.user && (
            <div className="mt-2 pt-2 border-t border-slate-100">
              <div className="text-[11px] uppercase text-slate-400 mb-1">Linked account</div>
              <KV k="User name" v={order.user.name} />
              <KV k="User mobile" v={order.user.mobile} />
              <KV k="Registered" v={order.user.registered_at && new Date(order.user.registered_at).toLocaleDateString()} />
            </div>
          )}
        </Section>

        <Section title="Items">
          <div className="space-y-2">
            {(order.items || []).map((it, i) => (
              <div key={i} className="flex items-start justify-between gap-3 text-sm">
                <div className="flex-1">
                  <p className="font-medium text-slate-900">{it.plan_title}</p>
                  <p className="text-xs text-slate-500">{it.file_type}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">{fmtINR(it.price_paise)}</p>
                  <p className="text-xs text-slate-500 inline-flex items-center gap-1">
                    <DownloadIcon className="w-3 h-3" />{it.download_count || 0}× downloaded
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100 text-sm space-y-1">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{fmtINR(order.subtotal_paise)}</span></div>
            <div className="flex justify-between text-slate-600"><span>GST</span><span>{fmtINR(order.gst_paise)}</span></div>
            <div className="flex justify-between font-bold text-slate-900 text-base"><span>Total</span><span>{fmtINR(order.amount)}</span></div>
          </div>
        </Section>

        <Section title="Payment">
          <KV k="Razorpay Order" v={order.razorpay_order_id} />
          <KV k="Razorpay Payment" v={order.razorpay_payment_id} />
          {order.failure_reason && <KV k="Failure reason" v={order.failure_reason} />}
        </Section>

        <Section title="Device & network">
          <KV k="IP at order create" v={order.ip_create} />
          <KV k="IP at verify" v={order.ip_verify} />
          <KV k="User agent" v={(order.ua_create || "").slice(0, 80)} />
        </Section>

        {order.downloads && order.downloads.length > 0 && (
          <Section title={`Download log (${order.downloads.length})`}>
            <div className="space-y-1 text-xs">
              {order.downloads.map(d => (
                <div key={d.id} className="flex justify-between gap-2">
                  <span className="text-slate-500">{d.file_type}</span>
                  <span className="text-slate-700">{d.timestamp && new Date(d.timestamp).toLocaleString()}</span>
                  <span className="text-slate-400 font-mono">{d.ip || "-"}</span>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
